import { create } from "zustand";
import type { ArenaState, Faction, Player, Creature, ArenaEvent } from "@shared/types";
import type { Snapshot } from "../lib/network";

interface ArenaStore {
  arenaId: string | null;
  playerId: string | null;
  playerName: string;
  faction: Faction;
  state: ArenaState | null;
  error: string | null;
  shopOpen: boolean;

  setIdentity: (arenaId: string, playerId: string, name: string, faction: Faction) => void;
  setState: (state: ArenaState | null) => void;
  /** apply a full or delta snapshot, returning the merged ArenaState */
  mergeSnapshot: (snap: Snapshot) => ArenaState;
  setError: (error: string | null) => void;
  setShopOpen: (open: boolean) => void;
  reset: () => void;
}

function mergeEntity<T extends { id: string }>(
  existing: T[],
  incoming: T[] | undefined,
): T[] {
  if (!incoming || incoming.length === 0) return existing;
  const map = new Map(existing.map((e) => [e.id, e]));
  for (const e of incoming) map.set(e.id, e);
  return [...map.values()];
}

function mergeEvents(existing: ArenaEvent[], incoming: ArenaEvent[] | undefined): ArenaEvent[] {
  if (!incoming || incoming.length === 0) return existing;
  const known = new Set(existing.map((e) => e.id));
  const fresh = incoming.filter((e) => !known.has(e.id));
  if (fresh.length === 0) return existing;
  return [...incoming, ...existing].slice(0, 60);
}

export const useArenaStore = create<ArenaStore>((set, get) => ({
  arenaId: null,
  playerId: null,
  playerName: "",
  faction: "dawn",
  state: null,
  error: null,
  shopOpen: false,

  setIdentity: (arenaId, playerId, name, faction) =>
    set({ arenaId, playerId, playerName: name, faction }),
  setState: (state) => set({ state }),
  mergeSnapshot: (snap) => {
    const prev = get().state;
    let next: ArenaState;
    if (snap.full && snap.state) {
      next = snap.state;
    } else if (!prev) {
      // no baseline yet; can't apply a delta
      next = {
        tickAt: Date.now(),
        tickCount: snap.tickCount ?? 0,
        phase: snap.phase ?? "active",
        matchEndsAt: snap.matchEndsAt ?? Date.now() + 8 * 60 * 1000,
        score: snap.score ?? { dawn: 0, dusk: 0 },
        players: snap.players ?? [],
        creatures: snap.creatures ?? [],
        arrows: snap.arrows ?? [],
        events: snap.events ?? [],
        createdAt: Date.now(),
      };
    } else {
      next = {
        ...prev,
        tickCount: snap.tickCount ?? prev.tickCount,
        phase: snap.phase ?? prev.phase,
        matchEndsAt: snap.matchEndsAt ?? prev.matchEndsAt,
        score: snap.score ?? prev.score,
        players: mergeEntity<Player>(prev.players, snap.players),
        creatures: mergeEntity<Creature>(prev.creatures, snap.creatures),
        arrows: snap.arrows ?? prev.arrows,
        events: mergeEvents(prev.events, snap.events),
      };
    }
    set({ state: next });
    return next;
  },
  setError: (error) => set({ error }),
  setShopOpen: (shopOpen) => set({ shopOpen }),
  reset: () =>
    set({
      arenaId: null,
      playerId: null,
      playerName: "",
      faction: "dawn",
      state: null,
      error: null,
      shopOpen: false,
    }),
}));
